import { Controller } from "@hotwired/stimulus";
import { WebAuthnSigner } from "@dfns/sdk-browser";
import { debugLog } from "../../../utils";
import { errorToastMessages, showErrorCard, showErrorToast } from "../../../errorsurfaces";
import { storage } from "../../../storageconstants";
import { detectBrowserMetadata } from "../../../utils/browserMetadataHandler";

export default class PasskeyController extends Controller {
  static values = {
    dfnsChallenge: String,
    session: String,
    code: String,
  }
  
  connect() {
    this.showLoadingScreen();
    this.getDFNSChallengeValue();
  }

  async getDFNSChallengeValue() {
    const metadata = detectBrowserMetadata();
    metadata.appType = "buy";
    const jsonMetadata = JSON.stringify(metadata);

    const request = await fetch("/get-dfns-challenge-value", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session": this.sessionValue,
        "X-Code": this.codeValue,
      },
      body: JSON.stringify({
        metadata: jsonMetadata,
      })
    });

    if (!request.ok) {
      this.hideLoadingScreen();
      const errorBody = await request.json().catch(() => ({}));
      if (errorBody.error_html) {
        Turbo.renderStreamMessage(errorBody.error_html);
      }
      return;
    }

    const response = await request.json();

    if (response.response_type === "dfns_key_user") {
      // Created new key credential
      if (response.redirect_url) {
        Turbo.visit(response.redirect_url);
      }
      return;
    }
    this.dfnsChallengeValue = response.dfns_challenge;
    this.handlePasskeyCreation();
  }

  async handlePasskeyCreation(_) {
    try {
      // Webauthn flow
      const webauthn = new WebAuthnSigner({
        relyingParty: {
          id: sessionStorage.getItem(storage.DOMAIN) ?? process.env.PARCEL_DFNS_RELYING_PARTY_ID,
          name: sessionStorage.getItem(storage.COMPANY_NAME) ?? process.env.PARCEL_DFNS_RELYING_PARTY_NAME,
        },
      });

      const parsedDFNSChallenge = JSON.parse(this.dfnsChallengeValue);

      const attestation = await webauthn.create(parsedDFNSChallenge);

      const responseDFNSRegister = await fetch("/dfns/register/complete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          signedChallenge: { firstFactorCredential: attestation },
          temporaryAuthenticationToken: parsedDFNSChallenge.temporaryAuthenticationToken,
          session: this.sessionValue,
        }),
      });

      if (!responseDFNSRegister.ok) {
        this.hideLoadingScreen();
        const errorBody = await responseDFNSRegister.json().catch(() => ({}));
        if (errorBody.error_html) {
          Turbo.renderStreamMessage(errorBody.error_html);
        } else {
          showErrorCard("wallet-setup-failed", { session: this.sessionValue });
        }
        return;
      }

      const responseDFNSRegisterData = await responseDFNSRegister.json();
      this.hideLoadingScreen();

      if (responseDFNSRegisterData.redirectPath) {
        Turbo.visit(responseDFNSRegister.redirectPath)
      }
    } catch (error) {
      debugLog("Error during passkey creation:", error);
      this.hideLoadingScreen();

      // Deliberate cancellation keeps the step and its CTA in place — a toast
      // is enough. Real failures get the wallet-setup-failed retry card.
      if (
        error.name === "NotAllowedError" ||
        error.message?.includes("NotAllowedError") ||
        error.name === "AbortError" ||
        error.name === "InvalidStateError"
      ) {
        showErrorToast(errorToastMessages.passkeyCancelled);
      } else {
        showErrorCard("wallet-setup-failed", { session: this.sessionValue });
      }
    }
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
